import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ArrowLeft, BadgeCheck, BarChart3, Download, Flag, Globe, History, LayoutDashboard, LifeBuoy, Loader2, LogOut, Megaphone, PieChart as PieIcon, Play, Reply, Send, ShieldCheck, Ticket, Trash2, UserPlus, Users, UsersRound, Wallet } from 'lucide-react';
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';
import { useLang } from '../i18n/LangContext';
import { AdminTickets } from '../components/AdminTickets';
import { AdminStats } from '../components/AdminStats';
import { AdminUsers } from '../components/AdminUsers';
import { AdminVerifications } from '../components/AdminVerifications';
import { AdminReports } from '../components/AdminReports';
import { AdminFinance } from '../components/AdminFinance';
import { AdminAudit } from '../components/AdminAudit';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'krug-admin-token';

const AdminPage = () => {
  const { t } = useLang();
  const a = t.admin;
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState(null);
  const [supportMsgs, setSupportMsgs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [supportFilter, setSupportFilter] = useState('all');
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [adminForm, setAdminForm] = useState({ email: '', password: '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '' });
  const [activeTab, setActiveTab] = useState('overview');
  const [unreadTickets, setUnreadTickets] = useState(0);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` }),
    [],
  );

  const loadData = useCallback(
    async (role) => {
      try {
        const res = await fetch(`${API}/api/admin/waitlist`, { headers: authHeaders() });
        if (res.status === 401) throw new Error('unauthorized');
        const data = await res.json();
        setItems(data.items || []);
        const sres = await fetch(`${API}/api/admin/waitlist/stats`, { headers: authHeaders() });
        if (sres.ok) setStats(await sres.json());
        const mres = await fetch(`${API}/api/admin/support`, { headers: authHeaders() });
        if (mres.ok) setSupportMsgs((await mres.json()).items || []);
        const ares = await fetch(`${API}/api/admin/analytics`, { headers: authHeaders() });
        if (ares.ok) setAnalytics(await ares.json());
        if (role === 'superadmin') {
          const ares = await fetch(`${API}/api/admin/admins`, { headers: authHeaders() });
          if (ares.ok) setAdmins((await ares.json()).items || []);
        }
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setSession(null);
      }
    },
    [authHeaders],
  );

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    fetch(`${API}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) throw new Error();
        const me = await res.json();
        setSession({ email: me.email, role: me.role });
        loadData(me.role);
      })
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, [loadData]);

  const fetchUnreadTickets = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/app-tickets`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const unread = (data.tickets || []).reduce((sum, t) => sum + (t.admin_unread || 0), 0);
      setUnreadTickets(unread);
    } catch {
      // servis podrške nedostupan — brojač ostaje nepromijenjen
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!session) return undefined;
    fetchUnreadTickets();
    const interval = setInterval(fetchUnreadTickets, 30000);
    return () => clearInterval(interval);
  }, [session, fetchUnreadTickets]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.token);
      setSession({ email: data.email, role: data.role });
      loadData(data.role);
    } catch {
      setLoginError(a.loginError);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setSession(null);
    setItems([]);
    setAdmins([]);
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm(`${a.deleteConfirm} ${entry.email}?`)) return;
    const res = await fetch(`${API}/api/admin/waitlist/${entry.id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (res.ok) {
      setItems((prev) => prev.filter((it) => it.id !== entry.id));
      toast.success(a.deleted);
      const sres = await fetch(`${API}/api/admin/waitlist/stats`, { headers: authHeaders() });
      if (sres.ok) setStats(await sres.json());
    } else {
      toast.error(a.deleteFail);
    }
  };

  const broadcast = async () => {
    if (!window.confirm(a.broadcastConfirm)) return;
    const res = await fetch(`${API}/api/admin/broadcast`, { method: 'POST', headers: authHeaders() });
    if (res.ok) {
      const data = await res.json();
      toast.success(`${a.broadcastQueued} ${data.queued}`);
    } else {
      toast.error(a.broadcastFail);
    }
  };

  const broadcastAndroid = async () => {
    if (!window.confirm(a.broadcastAndroidConfirm)) return;
    const res = await fetch(`${API}/api/admin/broadcast-android`, { method: 'POST', headers: authHeaders() });
    if (res.ok) {
      const data = await res.json();
      toast.success(`${a.broadcastQueued} ${data.queued}`);
    } else {
      let detail = a.broadcastFail;
      try {
        const err = await res.json();
        if (err.detail) detail = err.detail;
      } catch { /* zadrži podrazumijevanu poruku */ }
      toast.error(detail);
    }
  };

  const setMsgStatus = async (msg, status) => {
    const res = await fetch(`${API}/api/admin/support/${msg.id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      setSupportMsgs((prev) => prev.map((m) => (m.id === msg.id ? { ...m, status } : m)));
    }
  };

  const sendReply = async (msg) => {
    if (replyText.trim().length < 5) return;
    setReplySending(true);
    try {
      const res = await fetch(`${API}/api/admin/support/${msg.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ message: replyText.trim() }),
      });
      if (!res.ok) throw new Error();
      setSupportMsgs((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, status: 'resolved', reply: replyText.trim() } : m)),
      );
      setReplyOpen(null);
      setReplyText('');
      toast.success(a.replySent);
    } catch {
      toast.error(a.replyFail);
    } finally {
      setReplySending(false);
    }
  };

  const deleteSupportMsg = async (msg) => {
    if (!window.confirm(`${a.deleteConfirm} ${msg.email}?`)) return;
    const res = await fetch(`${API}/api/admin/support/${msg.id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (res.ok) {
      setSupportMsgs((prev) => prev.filter((m) => m.id !== msg.id));
      toast.success(a.deleted);
    } else {
      toast.error(a.deleteFail);
    }
  };

  const exportCsv = async () => {    const res = await fetch(`${API}/api/admin/waitlist/export`, { headers: authHeaders() });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'krug-waitlist.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    const res = await fetch(`${API}/api/admin/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(pwForm),
    });
    if (res.ok) {
      toast.success(a.passwordChanged);
      setPwForm({ current_password: '', new_password: '' });
    } else {
      toast.error(a.wrongCurrent);
    }
  };

  const removeAdmin = async (ad) => {
    if (!window.confirm(`${a.removeConfirm} ${ad.email}?`)) return;
    const res = await fetch(`${API}/api/admin/admins/${ad.id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (res.ok) {
      setAdmins((prev) => prev.filter((x) => x.id !== ad.id));
      toast.success(a.removed);
    } else {
      toast.error(a.removeFail);
    }
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    const res = await fetch(`${API}/api/admin/admins`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(adminForm),
    });
    if (res.status === 409) {
      toast.error(a.exists);
      return;
    }
    if (!res.ok) {
      toast.error(a.loginError);
      return;
    }
    toast.success(a.added);
    setAdminForm({ email: '', password: '' });
    const ares = await fetch(`${API}/api/admin/admins`, { headers: authHeaders() });
    if (ares.ok) setAdmins((await ares.json()).items || []);
  };

  const inputCls =
    'w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-krug-tealbright transition-colors duration-300';

  if (checking) {
    return (
      <div className="min-h-screen bg-krug-navy flex items-center justify-center">
        <Loader2 className="animate-spin text-krug-tealbright" size={28} />
      </div>
    );
  }

  if (!session) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-krug-navy flex items-center justify-center px-5">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm rounded-3xl glass-dark p-8 md:p-10"
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white p-1">
              <img src="/assets/krug-logo.png" alt="KRUG logo" className="w-full h-full object-contain" />
            </span>
            <div>
              <h1 className="font-heading text-xl font-extrabold text-white">{a.loginTitle}</h1>
              <p className="text-xs text-slate-500">{a.loginSub}</p>
            </div>
          </div>
          <div className="mt-8 space-y-4">
            <input
              type="email"
              required
              data-testid="admin-input-email"
              placeholder={a.email}
              value={loginForm.email}
              onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls}
            />
            <input
              type="password"
              required
              data-testid="admin-input-password"
              placeholder={a.password}
              value={loginForm.password}
              onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
              className={inputCls}
            />
            {loginError && (
              <p data-testid="admin-login-error" className="text-xs text-red-400">{loginError}</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full rounded-full bg-krug-gold py-3.5 font-heading font-bold text-krug-navy hover:bg-krug-goldbright disabled:opacity-60 transition-colors duration-300"
            >
              {loading ? <Loader2 size={18} className="mx-auto animate-spin" /> : a.login}
            </button>
          </div>
          <Link to="/" data-testid="admin-back-home" className="mt-6 inline-flex items-center gap-2 text-xs text-slate-500 hover:text-krug-tealbright transition-colors duration-300">
            <ArrowLeft size={14} />
            krug-app.eu
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-krug-navy">
      <header className="border-b border-white/10 bg-krug-deep/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white p-1">
              <img src="/assets/krug-logo.png" alt="KRUG logo" className="w-full h-full object-contain" />
            </span>
            <span className="font-heading font-extrabold text-white">{a.loginTitle}</span>
          </div>
          <div className="flex items-center gap-4">
            <span data-testid="admin-session-email" className="hidden sm:block text-xs text-slate-400">
              {session.email} · {session.role === 'superadmin' ? a.roleSuper : a.roleAdmin}
            </span>
            <button
              type="button"
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-slate-300 hover:text-white hover:border-krug-gold/50 transition-colors duration-300"
            >
              <LogOut size={14} />
              {a.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10 space-y-12">
        <div data-testid="admin-tabs" className="flex flex-wrap gap-2">
          {[
            ['overview', a.app.tabs.overview, LayoutDashboard],
            ['users', a.app.tabs.users, UsersRound],
            ['verifications', a.app.tabs.verifications, BadgeCheck],
            ['reports', a.app.tabs.reports, Flag],
            ['finance', a.app.tabs.finance, Wallet],
            ['tickets', a.app.tabs.tickets, Ticket],
            ['audit', a.app.tabs.audit, History],
            ['web', a.app.tabs.web, Globe],
          ].map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              data-testid={`tab-${key}-btn`}
              onClick={() => setActiveTab(key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-colors duration-300 ${
                activeTab === key ? 'bg-krug-gold text-krug-navy' : 'border border-white/15 text-slate-300 hover:text-white'
              }`}
            >
              <Icon size={15} />
              {label}
              {key === 'tickets' && unreadTickets > 0 && (
                <span
                  data-testid="tab-tickets-unread-badge"
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-extrabold"
                >
                  {unreadTickets}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && <AdminStats authHeaders={authHeaders} tx={a.app} />}
        {activeTab === 'users' && <AdminUsers authHeaders={authHeaders} tx={a.app} />}
        {activeTab === 'verifications' && <AdminVerifications authHeaders={authHeaders} tx={a.app} />}
        {activeTab === 'reports' && <AdminReports authHeaders={authHeaders} tx={a.app} />}
        {activeTab === 'finance' && <AdminFinance authHeaders={authHeaders} tx={a.app} />}
        {activeTab === 'audit' && <AdminAudit authHeaders={authHeaders} tx={a.app} />}
        {activeTab === 'tickets' && <AdminTickets authHeaders={authHeaders} tk={a.tickets} onDataChange={fetchUnreadTickets} />}
        {activeTab === 'web' && (
          <>
        <section>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-white flex items-center gap-3">
                <Users size={24} className="text-krug-tealbright" />
                {a.dashboard}
              </h2>
              <p data-testid="admin-waitlist-total" className="mt-2 text-sm text-krug-goldbright font-bold">
                {a.total}: {items.length}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {session.role === 'superadmin' && (
                <button
                  type="button"
                  data-testid="broadcast-btn"
                  onClick={broadcast}
                  className="inline-flex items-center gap-2 rounded-full bg-krug-gold px-5 py-2.5 text-sm font-bold text-krug-navy hover:bg-krug-goldbright transition-colors duration-300"
                >
                  <Megaphone size={16} />
                  {a.broadcastBtn}
                </button>
              )}
              {session.role === 'superadmin' && (
                <button
                  type="button"
                  data-testid="broadcast-android-btn"
                  onClick={broadcastAndroid}
                  className="inline-flex items-center gap-2 rounded-full border border-krug-gold/40 px-5 py-2.5 text-sm font-bold text-krug-goldbright hover:bg-krug-gold/10 transition-colors duration-300"
                >
                  <Play size={16} />
                  {a.broadcastAndroidBtn}
                </button>
              )}
              <button
                type="button"
                data-testid="export-csv-btn"
                onClick={exportCsv}
                className="inline-flex items-center gap-2 rounded-full bg-krug-teal px-5 py-2.5 text-sm font-bold text-white hover:bg-krug-tealbright hover:text-krug-navy transition-colors duration-300"
              >
                <Download size={16} />
                {a.exportCsv}
              </button>
            </div>
          </div>

          {stats && (stats.perDay.length > 0 || stats.perCountry.length > 0) && (
            <div data-testid="stats-section" className="mt-8">
              <h3 className="font-heading text-lg font-extrabold text-white flex items-center gap-2 mb-4">
                <BarChart3 size={19} className="text-krug-tealbright" />
                {a.statsTitle}
              </h3>
              <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5">
                <div className="rounded-2xl glass-dark p-6">
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-5">{a.perDay}</p>
                  <div data-testid="stats-per-day" className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.perDay}>
                        <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
                        <Tooltip
                          cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                          contentStyle={{ background: '#101E33', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, fontSize: 12 }}
                          labelStyle={{ color: '#94A3B8' }}
                        />
                        <Bar dataKey="count" fill="#0D9488" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="rounded-2xl glass-dark p-6">
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-5 flex items-center gap-2">
                    <PieIcon size={14} />
                    {a.perCountry}
                  </p>
                  <div data-testid="stats-per-country" className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stats.perCountry}
                          dataKey="count"
                          nameKey="country"
                          innerRadius="55%"
                          outerRadius="85%"
                          paddingAngle={3}
                          stroke="none"
                          label={({ country, count }) => `${country} (${count})`}
                          labelLine={false}
                          fontSize={11}
                          fill="#94A3B8"
                        >
                          {stats.perCountry.map((_, i) => (
                            <Cell key={i} fill={['#0D9488', '#F59E0B', '#D946EF', '#2DD4BF', '#EC4899', '#FBBF24'][i % 6]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ background: '#101E33', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, fontSize: 12 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10">
            <table data-testid="waitlist-table" className="w-full text-left text-sm">
              <thead>
                <tr className="bg-white/[0.04] text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3.5 font-bold">{a.colName}</th>
                  <th className="px-5 py-3.5 font-bold">{a.colEmail}</th>
                  <th className="px-5 py-3.5 font-bold">{a.colCity}</th>
                  <th className="px-5 py-3.5 font-bold">{a.colCountry}</th>
                  <th className="px-5 py-3.5 font-bold">{a.colNewsletter}</th>
                  <th className="px-5 py-3.5 font-bold">{a.colDate}</th>
                  <th className="px-5 py-3.5 font-bold" aria-label={a.delete} />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} data-testid="waitlist-empty" className="px-5 py-10 text-center text-slate-500">
                      {a.empty}
                    </td>
                  </tr>
                )}
                {items.map((it) => (
                  <tr key={it.id || it.email} className="border-t border-white/5 hover:bg-white/[0.03] transition-colors duration-200">
                    <td className="px-5 py-3.5 text-white font-medium">{it.name}</td>
                    <td className="px-5 py-3.5 text-slate-300">{it.email}</td>
                    <td className="px-5 py-3.5 text-slate-400">{it.city || '—'}</td>
                    <td className="px-5 py-3.5 text-slate-400">{it.country || '—'}</td>
                    <td className="px-5 py-3.5">{it.newsletter ? <span className="text-krug-tealbright">{a.yes}</span> : <span className="text-slate-600">{a.no}</span>}</td>
                    <td className="px-5 py-3.5 text-slate-500 text-xs">{it.created_at ? new Date(it.created_at).toLocaleString('bs-BA') : '—'}</td>
                    <td className="px-5 py-3.5">
                      <button
                        type="button"
                        data-testid={`delete-entry-${it.id}`}
                        onClick={() => deleteEntry(it)}
                        aria-label={`${a.delete} ${it.email}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/10 text-slate-500 hover:text-red-400 hover:border-red-400/50 transition-colors duration-300"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {analytics && (
          <section data-testid="analytics-section">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-white flex items-center gap-3">
              <Activity size={24} className="text-krug-goldbright" />
              {a.analyticsTitle}
            </h2>
            {analytics.totalViews === 0 ? (
              <p data-testid="analytics-empty" className="mt-6 rounded-2xl glass-dark px-6 py-8 text-center text-sm text-slate-500">
                {a.analyticsEmpty}
              </p>
            ) : (
              <>
                <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
                  {[
                    { label: a.totalViews, value: analytics.totalViews, testid: 'kpi-views' },
                    { label: a.totalClicks, value: analytics.totalClicks, testid: 'kpi-clicks' },
                    { label: a.uniqueVisitors, value: analytics.uniqueVisitors, testid: 'kpi-visitors' },
                    { label: a.viewsToday, value: analytics.viewsToday, testid: 'kpi-today' },
                    { label: a.activeNow, value: analytics.activeNow, testid: 'kpi-active', highlight: true },
                  ].map((k) => (
                    <div key={k.testid} data-testid={k.testid} className={`rounded-2xl glass-dark p-5 ${k.highlight ? 'border-emerald-400/40' : ''}`}>
                      <p className={`font-heading text-3xl font-extrabold tabular-nums ${k.highlight ? 'text-emerald-400' : 'text-krug-goldbright'}`}>{k.value}</p>
                      <p className="mt-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-bold">{k.label}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 grid lg:grid-cols-[1.4fr_1fr] gap-5">
                  <div className="rounded-2xl glass-dark p-6">
                    <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-5">{a.viewsPerDay}</p>
                    <div data-testid="analytics-chart" className="h-52">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.perDay}>
                          <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} />
                          <YAxis allowDecimals={false} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
                          <Tooltip
                            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                            contentStyle={{ background: '#101E33', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, fontSize: 12 }}
                            labelStyle={{ color: '#94A3B8' }}
                          />
                          <Bar dataKey="count" fill="#F59E0B" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="space-y-5">
                    <div className="rounded-2xl glass-dark p-6">
                      <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-4">{a.topPages}</p>
                      <ul data-testid="analytics-top-pages" className="space-y-2.5">
                        {analytics.topPages.map((p) => (
                          <li key={p.page} className="flex items-center justify-between text-sm">
                            <span className="text-slate-300">{p.page}</span>
                            <span className="font-bold text-krug-tealbright tabular-nums">{p.count}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-2xl glass-dark p-6">
                      <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-4">{a.topClicks}</p>
                      <ul data-testid="analytics-top-clicks" className="space-y-2.5">
                        {analytics.topClicks.map((c) => (
                          <li key={c.element} className="flex items-center justify-between text-sm gap-3">
                            <span className="text-slate-300 truncate">{c.element}</span>
                            <span className="font-bold text-krug-goldbright tabular-nums shrink-0">{c.count}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>
        )}

        <section data-testid="support-messages-section">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-white flex items-center gap-3">
              <LifeBuoy size={24} className="text-krug-tealbright" />
              {a.supportTitle}
              <span className="text-sm font-bold text-krug-goldbright">({supportMsgs.length})</span>
            </h2>
            <div data-testid="support-filters" className="flex gap-2">
              {[['all', a.filterAll], ['new', a.statusNew], ['resolved', a.statusResolved]].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`support-filter-${key}`}
                  onClick={() => setSupportFilter(key)}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors duration-300 ${
                    supportFilter === key ? 'bg-krug-gold text-krug-navy' : 'border border-white/15 text-slate-300 hover:text-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6 space-y-4">
            {supportMsgs.filter((m) => supportFilter === 'all' || (m.status || 'new') === supportFilter).length === 0 && (
              <p data-testid="support-messages-empty" className="rounded-2xl glass-dark px-6 py-8 text-center text-sm text-slate-500">
                {a.supportEmpty}
              </p>
            )}
            {supportMsgs
              .filter((m) => supportFilter === 'all' || (m.status || 'new') === supportFilter)
              .map((m) => {
                const status = m.status || 'new';
                return (
                  <article key={m.id} data-testid={`support-message-${m.id}`} className="rounded-2xl glass-dark p-6">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-heading text-base font-extrabold text-white flex items-center gap-2.5 flex-wrap">
                          {m.name}
                          <span
                            data-testid={`support-status-${m.id}`}
                            className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                              status === 'resolved'
                                ? 'bg-emerald-500/15 text-emerald-400'
                                : status === 'read'
                                  ? 'bg-krug-teal/15 text-krug-tealbright'
                                  : 'bg-krug-gold/15 text-krug-goldbright'
                            }`}
                          >
                            {status === 'resolved' ? a.statusResolved : status === 'read' ? a.statusRead : a.statusNew}
                          </span>
                        </p>
                        <a href={`mailto:${m.email}`} className="text-xs text-krug-tealbright hover:underline">{m.email}</a>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="rounded-full bg-krug-gold/15 px-3 py-1 text-[11px] font-bold text-krug-goldbright">{m.topic}</span>
                        <span className="text-xs text-slate-500">{m.created_at ? new Date(m.created_at).toLocaleString('bs-BA') : '—'}</span>
                        <button
                          type="button"
                          data-testid={`delete-support-${m.id}`}
                          onClick={() => deleteSupportMsg(m)}
                          aria-label={`${a.delete} ${m.email}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/10 text-slate-500 hover:text-red-400 hover:border-red-400/50 transition-colors duration-300"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <p className="mt-4 text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                    {m.reply && (
                      <p data-testid={`support-reply-shown-${m.id}`} className="mt-3 rounded-xl border-l-2 border-emerald-400/60 bg-emerald-500/5 px-4 py-3 text-xs text-slate-400 whitespace-pre-wrap">
                        {m.reply}
                      </p>
                    )}
                    <div className="mt-4 flex flex-wrap items-center gap-2.5">
                      <button
                        type="button"
                        data-testid={`reply-support-${m.id}`}
                        onClick={() => { setReplyOpen(replyOpen === m.id ? null : m.id); setReplyText(''); }}
                        className="inline-flex items-center gap-1.5 rounded-full bg-krug-gold px-4 py-2 text-xs font-bold text-krug-navy hover:bg-krug-goldbright transition-colors duration-300"
                      >
                        <Reply size={13} />
                        {a.replyBtn}
                      </button>
                      {status === 'new' && (
                        <button
                          type="button"
                          data-testid={`read-support-${m.id}`}
                          onClick={() => setMsgStatus(m, 'read')}
                          className="rounded-full border border-krug-tealbright/40 px-4 py-2 text-xs font-bold text-krug-tealbright hover:bg-krug-teal/10 transition-colors duration-300"
                        >
                          {a.markRead}
                        </button>
                      )}
                      {status !== 'resolved' && (
                        <button
                          type="button"
                          data-testid={`resolve-support-${m.id}`}
                          onClick={() => setMsgStatus(m, 'resolved')}
                          className="rounded-full border border-emerald-400/40 px-4 py-2 text-xs font-bold text-emerald-400 hover:bg-emerald-500/10 transition-colors duration-300"
                        >
                          {a.markResolved}
                        </button>
                      )}
                    </div>
                    {replyOpen === m.id && (
                      <div data-testid={`reply-form-${m.id}`} className="mt-4 space-y-3">
                        <textarea
                          rows={4}
                          data-testid={`reply-textarea-${m.id}`}
                          placeholder={a.replyPlaceholder}
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          className="w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-krug-tealbright resize-none transition-colors duration-300"
                        />
                        <button
                          type="button"
                          data-testid={`reply-send-${m.id}`}
                          disabled={replySending || replyText.trim().length < 5}
                          onClick={() => sendReply(m)}
                          className="inline-flex items-center gap-2 rounded-full bg-krug-teal px-5 py-2.5 text-xs font-bold text-white hover:bg-krug-tealbright hover:text-krug-navy disabled:opacity-50 transition-colors duration-300"
                        >
                          {replySending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                          {a.replySend}
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
          </div>
        </section>

        <section data-testid="security-section">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-white flex items-center gap-3">
            <ShieldCheck size={24} className="text-krug-tealbright" />
            {a.changePasswordTitle}
          </h2>
          <form data-testid="change-password-form" onSubmit={changePassword} className="mt-6 rounded-2xl glass-dark p-6 max-w-md space-y-4">
            <input
              type="password"
              required
              data-testid="change-password-current"
              placeholder={a.currentPassword}
              value={pwForm.current_password}
              onChange={(e) => setPwForm((f) => ({ ...f, current_password: e.target.value }))}
              className={inputCls}
            />
            <input
              type="password"
              required
              minLength={8}
              data-testid="change-password-new"
              placeholder={a.newPassword}
              value={pwForm.new_password}
              onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))}
              className={inputCls}
            />
            <button
              type="submit"
              data-testid="change-password-submit"
              className="rounded-full bg-krug-teal px-6 py-3 text-sm font-bold text-white hover:bg-krug-tealbright hover:text-krug-navy transition-colors duration-300"
            >
              {a.changePasswordBtn}
            </button>
          </form>
        </section>

        {session.role === 'superadmin' && (
          <section data-testid="admins-section">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-white flex items-center gap-3">
              <ShieldCheck size={24} className="text-krug-goldbright" />
              {a.adminsTitle}
            </h2>
            <p className="mt-2 text-xs text-slate-500">{a.onlySuper}</p>

            <div className="mt-6 grid lg:grid-cols-2 gap-8">
              <ul data-testid="admins-list" className="space-y-3">
                {admins.map((ad) => (
                  <li key={ad.id || ad.email} className="flex items-center justify-between rounded-2xl glass-dark px-5 py-4">
                    <span className="text-sm text-white">{ad.email}</span>
                    <span className="flex items-center gap-2.5">
                      <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${ad.role === 'superadmin' ? 'bg-krug-gold/15 text-krug-goldbright' : 'bg-krug-teal/15 text-krug-tealbright'}`}>
                        {ad.role === 'superadmin' ? a.roleSuper : a.roleAdmin}
                      </span>
                      {ad.role !== 'superadmin' && (
                        <button
                          type="button"
                          data-testid={`remove-admin-${ad.id}`}
                          onClick={() => removeAdmin(ad)}
                          aria-label={`${a.remove} ${ad.email}`}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-white/10 text-slate-500 hover:text-red-400 hover:border-red-400/50 transition-colors duration-300"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <form data-testid="add-admin-form" onSubmit={addAdmin} className="rounded-2xl glass-dark p-6 space-y-4 h-fit">
                <h3 className="font-heading text-lg font-extrabold text-white flex items-center gap-2">
                  <UserPlus size={18} className="text-krug-tealbright" />
                  {a.addAdminTitle}
                </h3>
                <input
                  type="email"
                  required
                  data-testid="add-admin-email"
                  placeholder={a.email}
                  value={adminForm.email}
                  onChange={(e) => setAdminForm((f) => ({ ...f, email: e.target.value }))}
                  className={inputCls}
                />
                <input
                  type="password"
                  required
                  minLength={8}
                  data-testid="add-admin-password"
                  placeholder={a.password}
                  value={adminForm.password}
                  onChange={(e) => setAdminForm((f) => ({ ...f, password: e.target.value }))}
                  className={inputCls}
                />
                <button
                  type="submit"
                  data-testid="add-admin-submit"
                  className="rounded-full bg-krug-gold px-6 py-3 text-sm font-bold text-krug-navy hover:bg-krug-goldbright transition-colors duration-300"
                >
                  {a.addAdminBtn}
                </button>
              </form>
            </div>
          </section>
        )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
